import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Icon,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Breadcrumbs, Button, Divider, showToast } from '@diamond/shared/ui';
import { AdminLayout, ExcelFileDropzone } from '@diamond/sol-admin/components';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { downloadBlob, getApiErrorMessage } from '@diamond/sol-admin/utils';
import { CloudDownloadOutlined, UploadFileOutlined } from '@mui/icons-material';
import { useCallback, useState } from 'react';

import { EXPORT_FILENAME, exportDailyStock, importDailyStock } from '../api';

const BREADCRUMBS = [
  {
    title: 'Beranda',
    link: '/',
  },
  {
    title: 'Daily Stock Config',
    link: '/daily-stock',
  },
];

export function DailyStockPage() {
  const toast = useToast();
  const session = useAuthStore();

  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDropzoneError = useCallback(
    (message: string) => {
      showToast(toast, 'error', message);
    },
    [toast]
  );

  const handleDownloadTemplate = useCallback(async () => {
    setIsDownloading(true);

    try {
      const blob = await exportDailyStock(session.access_token);
      downloadBlob(blob, EXPORT_FILENAME);
      showToast(toast, 'success', 'Template file berhasil diunduh');
    } catch (error) {
      showToast(
        toast,
        'error',
        getApiErrorMessage(error, 'Gagal mengunduh template file')
      );
    } finally {
      setIsDownloading(false);
    }
  }, [session.access_token, toast]);

  const handleUpload = useCallback(async () => {
    if (!file) {
      showToast(toast, 'error', 'Silakan pilih file terlebih dahulu');
      return;
    }

    setIsUploading(true);

    try {
      const response = await importDailyStock(session.access_token, file);
      const importedCount = response.data?.imported_count ?? 0;
      const message =
        importedCount > 0
          ? `${response.message} (${importedCount} baris berhasil diimpor)`
          : response.message;

      showToast(toast, 'success', message || 'File berhasil di-upload');
      setFile(null);
    } catch (error) {
      showToast(
        toast,
        'error',
        getApiErrorMessage(error, 'Gagal meng-upload file Daily Stock Config')
      );
    } finally {
      setIsUploading(false);
    }
  }, [file, session.access_token, toast]);

  return (
    <AdminLayout>
      <Box pb={4}>
        <Breadcrumbs links={BREADCRUMBS} useRouterDom />
      </Box>

      <Flex justify="space-between" align="center" gap={4} wrap="wrap">
        <Box>
          <Text fontSize="2xl" as="b" color="gray.800">
            Daily Stock Config
          </Text>
          <Text fontSize="sm" color="gray.600" mt={1}>
            Upload konfigurasi batas order harian produk melalui file Excel.
          </Text>
        </Box>

        <HStack spacing={3}>
          <Button
            variant="outline"
            onClick={handleDownloadTemplate}
            isLoading={isDownloading}
            isDisabled={isUploading}
          >
            <HStack spacing={2}>
              <Icon as={CloudDownloadOutlined} boxSize={4} />
              <Text>Download Template</Text>
            </HStack>
          </Button>
          <Button
            variant="solid"
            onClick={handleUpload}
            isLoading={isUploading}
            isDisabled={!file || isDownloading}
          >
            <HStack spacing={2}>
              <Icon as={UploadFileOutlined} boxSize={4} />
              <Text>Upload File</Text>
            </HStack>
          </Button>
        </HStack>
      </Flex>

      <Stack pt={4} pb={8}>
        <Divider />
      </Stack>

      <Stack
        spacing={4}
        border="1px"
        borderRadius="md"
        borderColor="gray.200"
        bg="white"
        px={{ base: 3, md: 5 }}
        py={{ base: 4, md: 5 }}
        boxShadow="sm"
      >
        <Box
          border="1px"
          borderColor="gray.200"
          borderRadius="md"
          p={4}
          bg="gray.50"
        >
          <Stack spacing={2}>
            <Text fontSize="lg" as="b" color="gray.800">
              Upload Daily Stock Configuration
            </Text>
            <Text fontSize="sm" color="gray.700">
              Upload file Excel untuk konfigurasi batas order harian produk
              (product daily order limit configurations).
            </Text>
            <Text fontSize="sm" color="gray.600">
              File ini digunakan untuk mengatur jumlah maksimum order per hari
              untuk setiap produk.
            </Text>
            <HStack spacing={2} flexWrap="wrap" pt={1}>
              <Badge colorScheme="purple" px={2} py={1} borderRadius="md">
                Format xlsx
              </Badge>
              <Badge colorScheme="orange" px={2} py={1} borderRadius="md">
                Max 2MB
              </Badge>
              <Badge colorScheme="teal" px={2} py={1} borderRadius="md">
                1 file per upload
              </Badge>
            </HStack>
          </Stack>
        </Box>

        <Alert
          status="warning"
          borderRadius="md"
          alignItems="flex-start"
          bg="#FFF7ED"
          border="1px solid"
          borderColor="#FED7AA"
          px={4}
          py={3}
        >
          <AlertIcon mt={0.5} color="#C2410C" />
          <Text
            fontSize="sm"
            color="#9A3412"
            lineHeight="1.5"
            fontWeight="medium"
          >
            Perhatian: Nilai stock pada file menggunakan magnify x1000.
            Pastikan quantity yang diinput mengikuti satuan tersebut.
          </Text>
        </Alert>

        <FormControl>
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
            File Daily Stock Config
          </FormLabel>
          <ExcelFileDropzone
            file={file}
            onFileChange={setFile}
            onFileRejected={handleDropzoneError}
            isDisabled={isDownloading}
            isUploading={isUploading}
          />
        </FormControl>
      </Stack>
    </AdminLayout>
  );
}
